import {
	Component,
	Inject,
	ViewChild,
	ElementRef,
	OnInit,
	ChangeDetectionStrategy,
	OnDestroy
} from "@angular/core";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { AuthService } from "@royalty/service/api";
import { AuthenticationService } from "@royalty/service/auth";
import { debounceTime, distinctUntilChanged, finalize } from "rxjs/operators";
import { UserInfo } from "@royalty/model";
import { UserSettingService } from "@royalty/service";
import { Subject } from "rxjs";
import { AppUtils } from "src/app/core/utils/app-utils";

export interface StatusLogin {
	status: boolean;
	message?: string;
}

export interface LoginData {
	pin: string;
	username: string;
	password: string;
}

export interface Keyboard {
	text: string;
	value: number;
}

@Component({
	selector: "keyboard-login",
	styleUrls: ["./login.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	templateUrl: "./login.html"
})
export class LoginDialogComponent implements OnInit, OnDestroy {
	public isMobile: boolean = false;
	@ViewChild("yourPinFocus", { static: true }) yourPinFocus: ElementRef;
	@ViewChild("yourUsernameFocus", { static: true })
	yourUsernameFocus: ElementRef;

	keyboards: Keyboard[] = [
		{ text: "1", value: 1 },
		{ text: "2", value: 2 },
		{ text: "3", value: 3 },
		{ text: "4", value: 4 },
		{ text: "5", value: 5 },
		{ text: "6", value: 6 },
		{ text: "7", value: 7 },
		{ text: "8", value: 8 },
		{ text: "9", value: 9 },
		{ text: "0", value: 0 }
	];

	loginData: LoginData = { pin: "", username: "", password: "" };
	private _loginTermStream$ = new Subject<LoginData>();

	constructor(
		public dialogRef: MatDialogRef<LoginDialogComponent>,
		@Inject(MAT_DIALOG_DATA) public data: StatusLogin,
		private authService: AuthService,
		private authenticationService: AuthenticationService,
		private userSettingService: UserSettingService
	) {}

	ngOnInit(): void {
		this._loginTermStream$
			.pipe(
				debounceTime(300),
				distinctUntilChanged(),
				finalize(() => {})
			)
			.subscribe((loginData: LoginData) => {
				this.authService.login(loginData).subscribe(
					(userInfo: UserInfo) => {
						this.authenticationService.storeUserInfo(userInfo);
						this.data.status = true;

						this.dialogRef.close(this.data);

						this.authenticationService.init();

						this.userSettingService.useDefaultLanguage();

						this.authenticationService.scheduleRefresh();
					},
					error => {}
				);
			});
	}

	private trySupportLoginByPasswordOnMobile() {
		const outerWidth = window.outerWidth;
		this.isMobile = outerWidth < 600;
		if (this.isMobile) {
			this.yourUsernameFocus.nativeElement.focus();
		} else {
			if (outerWidth > 980) {
				this.yourPinFocus.nativeElement.focus();
			}
		}
	}

	onNoClick(): void {
		this.dialogRef.close();
	}

	clickOnNumber(keyboard: Keyboard, e: KeyboardEvent): void {
		if (typeof this.loginData.pin === "undefined") {
			this.loginData.pin = keyboard.text;
		} else {
			this.loginData.pin += keyboard.text;
		}
		this.checkPinAndAutoLogin(e);
		this.trySupportLoginByPasswordOnMobile();
	}

	clear() {
		this.loginData.pin = this.loginData.pin
			? this.loginData.pin.slice(0, -1)
			: "";
		this.trySupportLoginByPasswordOnMobile();
	}

	clearAll() {
		this.loginData.pin = "";
		this.trySupportLoginByPasswordOnMobile();
	}

	onLogin(body: LoginData) {
		this._loginTermStream$.next(body);
	}

	valuechange(e: KeyboardEvent) {
		this.checkPinAndAutoLogin(e);
	}

	private checkPinAndAutoLogin(e: KeyboardEvent) {
		if (e && e.keyCode === 13) {
			this.loginByEnter();
		} else {
			this.loginByLength();
		}
	}

	private loginByLength(): void {
		if (this.loginData && this.loginData.pin) {
			if (this.loginData.pin.length > 3) {
				this.onLogin(this.prepareDataForLogin());
			}
		}
	}

	private loginByEnter(): void {
		this.onLogin(this.prepareDataForLogin());
	}

	private prepareDataForLogin(): LoginData {
		// Need check & remove some characters specials when using card
		if (this.loginData) {
			let newPin = this.loginData.pin;
			const firstCharacter = newPin.substring(0, 1);
			if (firstCharacter === "%") {
				newPin = newPin.slice(1, newPin.length);
			}
			const lastCharacter = newPin.substring(newPin.length - 1);
			if (lastCharacter === "?") {
				newPin = newPin.slice(0, -1);
			}
			if (!this.isMobile) {
				if (newPin.length < 4) { return null; }
			}
			const body = {
				username: this.loginData.username,
				password: this.loginData.password,
				pin: newPin.toLowerCase()
			} as LoginData;
			return body;
		}
		return null;
	}

	public ngOnDestroy() {
		this._loginTermStream$ && this._loginTermStream$.unsubscribe();
	}
}
